import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl, errMsg } from "@/lib/api";
import YourStatsCard from "@/components/YourStatsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarPlus, Clock, CheckCircle2, XCircle, Plus, Pencil, Upload, Trash2, MapPin, Info, Building2, Phone, Mail, Link as LinkIcon, Globe, FileText, Tag } from "lucide-react";
import InviteTrialBadge from "@/components/InviteTrialBadge";

const badge = (s) => {
  if (s === "approved") return { icon: CheckCircle2, cls: "bg-sage-light text-sage-dark", label: "Approved" };
  if (s === "rejected") return { icon: XCircle, cls: "bg-terracotta-light text-terracotta-dark", label: "Rejected" };
  return { icon: Clock, cls: "bg-amber-100 text-amber-700", label: "Pending review" };
};

const EMPTY_EDIT = {
  company_logo: "", organizer_name: "", event_name: "", about_event: "",
  event_date: "", event_time: "", event_photo: "", location: "",
  contact_number: "", email: "", social_links: "", website: "",
};

// Populate the edit form from an event doc, tolerating legacy field names.
const seedFromEvent = (e) => ({
  company_logo: e.company_logo || "",
  organizer_name: e.organizer_name || e.creator_name || "",
  event_name: e.event_name || e.title || "",
  about_event: e.about_event || e.description || "",
  event_date: e.event_date || e.date || "",
  event_time: e.event_time || e.start_time || "",
  event_photo: e.event_photo || e.image_url || "",
  location: e.location || "",
  contact_number: e.contact_number || "",
  email: e.email || e.contact_email || "",
  social_links: e.social_links || "",
  website: e.website || "",
});

export default function EventCreatorPortal() {
  const [events, setEvents] = useState([]);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(null); // event being edited
  const [form, setForm] = useState(EMPTY_EDIT);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const logoRef = useRef(null);
  const photoRef = useRef(null);

  const load = () => {
    api.get("/event-creator/my-events").then((r) => setEvents(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/role-profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const openEdit = (e) => {
    setEditing(e);
    setForm(seedFromEvent(e));
  };

  const closeEdit = () => {
    setEditing(null);
    setForm(EMPTY_EDIT);
  };

  const upload = async (file, kind, setField) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB)");
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/event-creator/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setField(data.path);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading("");
    }
  };

  const submit = async () => {
    if (!form.organizer_name.trim()) return toast.error("Organizer / business name is required");
    if (!form.event_name.trim()) return toast.error("Event name is required");
    setSaving(true);
    try {
      await api.post(`/event-creator/events/${editing.event_id}/edit`, form);
      toast.success("Edit submitted for admin approval");
      closeEdit();
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not submit edit"));
    } finally {
      setSaving(false);
    }
  };

  const ImagePicker = ({ label, testid, value, onChange, kind, inputRef }) => (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        {value ? (
          <div className="relative w-20 h-20 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
            <img src={fileUrl(value)} alt={label} className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
            <button type="button" onClick={() => onChange("")} className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove image" data-testid={`${testid}-remove`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
            <Upload className="w-5 h-5" />
          </div>
        )}
        <div>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0], kind, onChange)} data-testid={`${testid}-input`} />
          <Button type="button" size="sm" variant="outline" className="rounded-full border-sage/40 text-forest" onClick={() => inputRef.current?.click()} disabled={uploading === kind} data-testid={`${testid}-btn`}>
            <Upload className="w-3.5 h-3.5 mr-2" />{uploading === kind ? "Uploading..." : (value ? "Replace" : "Upload")}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-10" data-testid="event-creator-portal">
      <div
        className="mb-6 rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/30 p-4 flex items-start gap-3"
        data-testid="ec-event-limit-notice"
        role="status"
      >
        <Info className="w-5 h-5 text-terracotta-dark shrink-0 mt-0.5" />
        <p className="text-sm text-forest/80 leading-relaxed" data-testid="ec-event-limit-notice-text">
          Your subscription includes up to 3 active events at a time. Any additional events loaded beyond 3 will be charged at R299 per month per event.
        </p>
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeEdit(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ec-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-forest">Edit event</DialogTitle>
            <DialogDescription className="text-forest/60 text-sm">
              Changes are sent to an admin for approval. Your event stays live in its current form until the admin approves your edits.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <ImagePicker label="Company Logo" testid="ec-edit-logo" value={form.company_logo}
              onChange={(v) => setForm((f) => ({ ...f, company_logo: v }))} kind="logo" inputRef={logoRef} />

            <div>
              <Label>Organizer / Business Name*</Label>
              <Input value={form.organizer_name} onChange={(e) => setForm({ ...form, organizer_name: e.target.value })}
                data-testid="ec-edit-organizer-name" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div>
              <Label>Event Name*</Label>
              <Input value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                data-testid="ec-edit-event-name" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div>
              <Label>About Event</Label>
              <Textarea value={form.about_event} onChange={(e) => setForm({ ...form, about_event: e.target.value })}
                data-testid="ec-edit-about-event" rows={4} className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Event Date</Label>
                <Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                  data-testid="ec-edit-event-date" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
              </div>
              <div>
                <Label>Event Time</Label>
                <Input type="time" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })}
                  data-testid="ec-edit-event-time" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
              </div>
            </div>

            <ImagePicker label="Event Photo / Add" testid="ec-edit-photo" value={form.event_photo}
              onChange={(v) => setForm((f) => ({ ...f, event_photo: v }))} kind="event_photo" inputRef={photoRef} />

            <div>
              <Label className="flex items-center gap-1"><MapPin className="w-4 h-4 text-sage-dark" /> Location of Event</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                data-testid="ec-edit-location" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div>
              <Label>Contact Number</Label>
              <Input value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                data-testid="ec-edit-contact-number" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div>
              <Label>Email Address</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="ec-edit-email" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div>
              <Label>Social Links</Label>
              <Input value={form.social_links} onChange={(e) => setForm({ ...form, social_links: e.target.value })}
                data-testid="ec-edit-social-links" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>

            <div>
              <Label>Website</Label>
              <Input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
                data-testid="ec-edit-website" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-full" onClick={closeEdit} data-testid="ec-edit-cancel-btn">Cancel</Button>
            <Button className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={submit} disabled={saving} data-testid="ec-edit-submit-btn">
              {saving ? "Submitting..." : "Submit for approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-3 mb-8">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><CalendarPlus className="w-5 h-5 text-white" /></span>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-black text-forest">Event Creator dashboard</h1>
          <p className="text-sm text-forest/60">Welcome back{profile?.profile?.business_name ? `, ${profile.profile.business_name}` : ""}</p>
          <InviteTrialBadge />
        </div>
        <Link to="/event-creator/new-event">
          <Button className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white" data-testid="ec-new-event-btn"><Plus className="w-4 h-4 mr-1" /> Create event</Button>
        </Link>
      </div>

      <YourStatsCard />

      {profile?.profile && (() => {
        const p = profile.profile || {};
        const businessName = p.business_name || p.organizer_name || "";
        const eventTypes = (p.event_name || "")
          .split("|")
          .map((s) => s.trim())
          .filter(Boolean);
        const logo = p.company_logo || "";
        const hasAnything = businessName || eventTypes.length || logo || p.tagline;
        if (!hasAnything) return null;
        return (
          <section className="mb-10" data-testid="ec-business-profile">
            <h2 className="font-heading font-bold text-forest text-lg mb-4">Your Business Profile</h2>
            <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm flex items-start gap-5">
              {logo ? (
                <img
                  src={fileUrl(logo)}
                  alt="Company logo"
                  className="w-20 h-20 rounded-2xl object-cover border border-sage/30 shrink-0"
                  data-testid="ec-profile-logo"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0" data-testid="ec-profile-logo-empty">
                  <Building2 className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                {businessName && (
                  <p className="text-lg font-heading font-bold text-forest break-words" data-testid="ec-profile-business-name">{businessName}</p>
                )}
                {p.tagline && (
                  <p className="text-sm text-forest/70 italic break-words mt-0.5" data-testid="ec-profile-tagline">{p.tagline}</p>
                )}
                {eventTypes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="ec-profile-event-types">
                    {eventTypes.map((t) => (
                      <span key={t} className="text-xs font-semibold text-sage-dark bg-sage-light/70 border border-sage/30 rounded-full px-2.5 py-1">{t}</span>
                    ))}
                  </div>
                )}
                <div className="mt-4">
                  <Link to="/event-creator-profile">
                    <Button size="sm" variant="outline" className="rounded-full border-sage/40 text-forest hover:bg-sage-light" data-testid="ec-edit-profile-btn">
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit profile
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      <section>
        <h2 className="font-heading font-bold text-forest text-lg mb-4">Your submitted events</h2>
        {events.length === 0 ? (
          <div className="bg-white/70 border border-sage/20 rounded-3xl p-8 text-center text-forest/60" data-testid="ec-empty">
            You haven't submitted any events yet. Click <strong>Create event</strong> to get started.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {events.map((e) => {
              const b = badge(e.status);
              const isPending = e.status !== "approved" && e.status !== "rejected";
              return (
                <div key={e.event_id} className={`bg-white/80 border border-sage/20 rounded-2xl p-5 shadow-sm transition-opacity ${isPending ? "opacity-60 grayscale" : ""}`} data-testid={`ec-event-${e.event_id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-heading font-bold text-forest">{e.event_name || e.title}</h3>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold ${b.cls}`}>
                      <b.icon className="w-3 h-3" /> {b.label}
                    </span>
                  </div>
                  <p className="text-sm text-forest/70 mb-1">{e.event_date || e.date}{(e.event_time || e.start_time) ? ` · ${e.event_time || e.start_time}` : ""}</p>
                  <p className="text-sm text-forest/60">{e.location}</p>
                  {e.pending_edit && (
                    <span className="mt-3 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-bold bg-amber-100 text-amber-800" data-testid={`ec-edit-pending-${e.event_id}`}>
                      <Clock className="w-3 h-3" /> Edit awaiting admin approval
                    </span>
                  )}
                  <div className="mt-4">
                    <Button size="sm" variant="outline" className="rounded-full border-sage/40 text-forest hover:bg-sage-light hover:text-forest" onClick={() => openEdit(e)} disabled={!!e.pending_edit} data-testid={`ec-edit-btn-${e.event_id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1.5" /> {e.pending_edit ? "Edit pending" : "Edit"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
